import { I18nThemed } from 'interfaces/mixins';
import { DNode, WidgetProperties, LocalizedMessages } from '@dojo/framework/core/interfaces';
import { storeInject } from '@dojo/framework/stores/StoreInjector';
import Store from '@dojo/framework/stores/Store';
import { Bundle, Messages } from '@dojo/framework/i18n/i18n';
import { v, w } from '@dojo/framework/core/vdom';
import { theme } from '@dojo/framework/core/mixins/Themed';
import { beforeProperties } from '@dojo/framework/core/decorators/beforeProperties';

import { userstore, rcslocationChangeProcess, SiteMeta } from 'lcc-state';
import { publish } from 'pub-sub';
import { Keys } from '@holmescorp/common';

import rcssvc from '../support/rcs-svc';

import { Backview } from './backview';
import { FormLabel } from './form-label';
import { FormLikert } from './form-likert';
import { FormSelection } from './form-selection';
import { FormString } from './form-string';
import { FormText } from './form-text';
import { FormFolder } from './form-folder';
import { Document } from './document';
import { FormThanksPage } from './form-thanks-page';
import { PageNotFound } from './page-not-found';

import css from '../css/resource-center.m.css';

export interface ResourceCenterProperties {
	baseurl: string;
	url: { [key: string]: string };
	dynls: Map<string, Bundle<Messages>>;
	title?: string;
}

let useheadless: boolean;
const setHeadless = async () => {
	try {
		useheadless = await rcssvc.use_headless();
	} catch (error) {
		useheadless = false;
	}
};

const formActions: { [key: string]: any } = {
	Document: Document,
	FormFolder: FormFolder,
	FormTextField: FormText,
	FormStringField: FormString,
	FormSelectionField: FormSelection,
	FormLikertField: FormLikert,
	FormLabelField: FormLabel,
	FormThanksPage: FormThanksPage,
	PageNotFound: PageNotFound
};

@theme(css)
@storeInject({
	name: 'lcc-state',
	paths: [['dynls']],
	getProperties: (store: Store<SiteMeta>) => {
		const { get, path } = store;
		return {
			dynls: get(path('dynls'))
		};
	}
})
export class ResourceCenter extends I18nThemed<any> {
	currentUrl: string;
	baseurl: string;
	url: string;
	lastContent: DNode;
	storehnd: { remove: () => void } | undefined;

	constructor() {
		super();
		if (useheadless === undefined) {
			setHeadless();
		}
		this.currentUrl = '';
		this.baseurl = '';
		this.url = `${window.hcstatic.portal_url}/common/rc`;
		this.lastContent = null;
	}

	@beforeProperties()
	protected set_properties(
		properties: ResourceCenterProperties & WidgetProperties
	): ResourceCenterProperties & WidgetProperties {
		const { get, path } = userstore;
		let currentLocation = get(path('rcslocation'));
		if (!currentLocation[properties.baseurl]) {
			rcslocationChangeProcess(userstore)({
				baseurl: `${properties.baseurl}`,
				location: `${properties.url}`
			});
			currentLocation = get(path('rcslocation'));
		}
		properties.url = currentLocation;
		return properties;
	}

	onAttach(): void {
		const { onChange, path } = userstore;
		this.storehnd = onChange(path('rcslocation'), () => {
			this.invalidate();
		});
	}

	onDetach(): void {
		this.storehnd && this.storehnd.remove();
		this.storehnd = undefined;
	}

	protected get_settings_nls(): LocalizedMessages<Messages> {
		const { dynls } = this.properties;
		const bundle = dynls.get('settings');
		if (!bundle) {
			return { isPlaceholder: true, format: () => '', messages: {} as Messages };
		}
		return this.localizeBundle(bundle);
	}

	protected render_content(): DNode {
		const { messages } = this.get_settings_nls();
		const { title: mediaTitle } = this.properties;
		const { association } = messages;
		const portalurl = window.hcstatic.portal_url!;
		const urlparts = /^(https?):\/\/([a-z.-]*)\//.exec(portalurl);
		const urischeme = urlparts![1];
		const hostname = `${urlparts![2]}`;
		const rest = /^\/common\/(.*)$/.exec(
			this.url.startsWith(portalurl) ? this.url.slice(portalurl.length) : this.url
		)![1];
		this.currentUrl = this.url;

		// const assoc = window.hcstatic.association?.toUpperCase();
		const product = window.hcstatic.portal_site?.toUpperCase();
		let title = mediaTitle ? mediaTitle : 'Resource Center';
		if (this.currentUrl.includes('bask')) {
			title = 'Body of Applied Skills and Knowledge';
		}

		let rcsTitle = '';

		if (association) {
			rcsTitle += association + ' - ';
		}
		if (product) {
			rcsTitle += product + ' - ';
		}
		rcsTitle += title;

		document.title = rcsTitle;

		const loader = async () => {
			let response = await rcssvc.load_path({ rest, hostname, urischeme });
			// if it's a folder, drill down to first item
			const folderish = response.is_folderish;
			let objtype = response['@type'];
			if (folderish && objtype !== 'FormFolder' && response.items.length > 0) {
				const subid = response.items[0]['@id'];
				const rest = /^.*\/common\/(.*)$/.exec(subid)![1];
				response = await rcssvc.load_path({ rest, hostname, urischeme });
				objtype = response['@type'];
			}
			if (formActions[objtype]) {
				response['baseurl'] = this.baseurl;
				const overrideurl = objtype === 'FormThanksPage' ? this.baseurl : '';
				return v('div', {}, [
					w(Backview, { baseurl: this.baseurl, overrideurl }),
					w(formActions[objtype], response)
				]);
			} else {
				return w(PageNotFound, response);
			}
		};

		loader().then((kids: DNode) => {
			this.lastContent = kids;
			this.invalidate();
		});

		return null;
	}

	protected render(): DNode {
		const { url, baseurl } = this.properties;
		if (!url) {
			return null;
		}

		const portal_url = window.hcstatic.portal_url;
		const uri = url[baseurl];
		this.url = `${portal_url}${uri}`;
		this.baseurl = baseurl;

		if (this.url !== this.currentUrl) {
			this.lastContent = this.render_content();
		}

		const linkHandler = (event: MouseEvent | KeyboardEvent) => {
			const { target } = event;
			const targetTag = (target as HTMLElement).tagName;
			if (targetTag !== 'INPUT' && targetTag !== 'LABEL') {
				event.stopPropagation();
				event.preventDefault();
			}
			if (targetTag === 'A') {
				const ele = target as unknown as HTMLAnchorElement;

				const portalurl = window.hcstatic.portal_url || 'NeverState';
				const ploneurl = portalurl.replace(window.hcstatic.sitealias!, window.hcstatic.portal_site!);
				const fullurl = ele.href;
				let url = fullurl.startsWith(ploneurl) ? fullurl.slice(ploneurl.length) : null;
				if (url === null) {
					url = fullurl;
				} else {
					url = url.split('/').slice(2).join('/');
				}
				console.log(`routing to ${url}`);
				if (url.startsWith('common')) {
					rcslocationChangeProcess(userstore)({
						baseurl: `${baseurl}`,
						location: `/${url}`
					});
					// publish(`site/${url}`);
				} else if (url.startsWith('video')) {
					const videoNum = url.split('/').pop();
					publish(`site/video/${videoNum}`);
				} else {
					window.open(url, '_blank');
				}
			}
		};

		return v(
			'div',
			{
				classes: [this.theme(css.root)],
				onclick: linkHandler,
				onkeydown: (event) => {
					switch (event.key) {
						case Keys.Enter:
						case Keys.Space:
						case Keys.Spacebar:
							event.preventDefault();
							linkHandler(event);
							break;
					}
				}
			},
			[this.lastContent]
		);
	}
}

export default ResourceCenter;
